/**
 * BOM Extraction Page
 * 
 * Main container for the BOM extraction feature.
 * Supports THREE extraction methods:
 * 1. PDF Text Layer - Best for PDFs with selectable text (most accurate)
 * 2. OCR + Smart Mapping - For scanned PDFs, uses Tesseract.js
 * 3. AI/LLM (GPT-4) - Most powerful, handles any format (requires API key)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import PdfUploader from './PdfUploader';
import PdfRegionSelector from './PdfRegionSelector';
import ExtractionResultsTable from './ExtractionResultsTable';
import { useToast } from '../../../hooks/useToast';
import { performOcrOnRegion, initializeOcr } from '../../../utils/localOcr';
import { parseOcrToTable } from '../../../utils/bomTableParser';
import { extractTextFromRegion } from '../../../utils/pdfTextExtractor';
import { smartParse, BOM_FORMATS } from '../../../utils/bomColumnMapper';
import { 
  processWithLlm, 
  isLlmConfigured, 
  setApiKey, 
  getApiKey,
  validateApiKey,
  LLM_PROVIDERS,
  getLlmProvider,
  setLlmProvider
} from '../../../utils/llmBomProcessor';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

// Extraction methods
const EXTRACTION_METHODS = {
  PDF_TEXT: {
    id: 'pdf_text',
    name: 'PDF Text Layer',
    description: 'Best for PDFs with selectable/copyable text. Most accurate and fastest.',
    icon: '📄',
    recommended: true
  },
  OCR_SMART: {
    id: 'ocr_smart',
    name: 'OCR + Smart Mapping',
    description: 'For scanned PDFs. Uses Tesseract.js OCR with intelligent column detection.',
    icon: '🔍',
    recommended: false
  },
  LLM_GPT4: {
    id: 'llm_gpt4',
    name: 'AI/LLM (FREE with Groq!)',
    description: 'Most powerful. Uses Groq (free) or OpenAI GPT-4. Understands context and handles complex layouts.',
    icon: '🤖',
    recommended: false,
    requiresApiKey: true
  }
};

// Workflow steps
const STEPS = {
  UPLOAD: 'upload',
  DEFINE_REGION: 'define_region',
  PROCESSING: 'processing',
  RESULTS: 'results'
};

const BomExtractionPage = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();
  const { toast, showToast } = useToast();
  const { viewers, isConnected, connectionState } = usePresence('bomExtraction', jobNumber || 'global', true);
  
  // State
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [regionCoords, setRegionCoords] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [extractions, setExtractions] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processingJobId, setProcessingJobId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Local processing state
  const [localFiles, setLocalFiles] = useState([]);
  const [selectedLocalFile, setSelectedLocalFile] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [useLocalMode, setUseLocalMode] = useState(true); // Default to local mode
  const pdfCanvasGetterRef = useRef(null);
  
  // Extraction method state
  const [extractionMethod, setExtractionMethod] = useState(EXTRACTION_METHODS.PDF_TEXT.id);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyValid, setApiKeyValid] = useState(isLlmConfigured());
  const [processingStatus, setProcessingStatus] = useState('');
  const [selectedLlmProvider, setSelectedLlmProvider] = useState(getLlmProvider());

  // Initialize OCR on mount
  useEffect(() => {
    initializeOcr().catch(err => {
      console.warn('OCR pre-initialization failed (will init on first use):', err);
    });
  }, []);

  // Check service status on mount
  useEffect(() => {
    checkServiceStatus();
    loadTemplates();
    loadExtractions();
  }, [jobNumber]);

  const checkServiceStatus = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/bom-extraction/status');
      setServiceStatus(response.data);
      // If Azure services aren't configured, force local mode
      if (!response.data?.services?.documentIntelligence) {
        setUseLocalMode(true);
      }
    } catch (err) {
      console.error('Error checking service status:', err);
      setServiceStatus({ services: { documentIntelligence: false, llmProcessor: false } });
      setUseLocalMode(true); // Force local mode on error
    } finally {
      setLoading(false);
    }
  };
  
  // Handle local file selection (no upload to server needed)
  const handleLocalFilesSelected = useCallback((files) => {
    const pdfFiles = Array.from(files).filter(f => f.type === 'application/pdf');
    setLocalFiles(pdfFiles);
    if (pdfFiles.length > 0) {
      setSelectedLocalFile(pdfFiles[0]);
      setCurrentStep(STEPS.DEFINE_REGION);
      showToast(`${pdfFiles.length} file(s) selected for local processing`, 'success');
    }
  }, [showToast]);
  
  // Handle API key save
  const handleSaveApiKey = async () => {
    if (!apiKeyInput.trim()) {
      showToast('Please enter an API key', 'warning');
      return;
    }
    
    setProcessingStatus('Validating API key...');
    const isValid = await validateApiKey(apiKeyInput.trim(), selectedLlmProvider);
    
    if (isValid) {
      setLlmProvider(selectedLlmProvider);
      setApiKey(apiKeyInput.trim(), selectedLlmProvider);
      setApiKeyValid(true);
      setShowApiKeyModal(false);
      setApiKeyInput('');
      showToast(`API key saved for ${LLM_PROVIDERS[selectedLlmProvider.toUpperCase()]?.name}!`, 'success');
    } else {
      showToast('Invalid API key. Please check and try again.', 'error');
    }
    setProcessingStatus('');
  };

  // Main extraction handler - routes to appropriate method
  const handleLocalExtraction = async () => {
    if (!regionCoords) {
      showToast('Please define a region first', 'warning');
      return;
    }

    // Check if LLM method requires API key
    if (extractionMethod === EXTRACTION_METHODS.LLM_GPT4.id && !isLlmConfigured()) {
      setShowApiKeyModal(true);
      return;
    }

    setProcessing(true);
    setCurrentStep(STEPS.PROCESSING);
    setOcrProgress(0);
    setProcessingStatus('Starting extraction...');

    try {
      let extraction;
      const filename = selectedLocalFile?.name || selectedUpload?.original_filename || 'Unknown';

      switch (extractionMethod) {
        case EXTRACTION_METHODS.PDF_TEXT.id:
          extraction = await extractWithPdfText(filename);
          break;
        case EXTRACTION_METHODS.OCR_SMART.id:
          extraction = await extractWithOcr(filename);
          break;
        case EXTRACTION_METHODS.LLM_GPT4.id:
          extraction = await extractWithLlm(filename);
          break;
        default:
          extraction = await extractWithPdfText(filename);
      }

      setExtractions(prev => [extraction, ...prev]);
      setCurrentStep(STEPS.RESULTS);
      const itemCount = extraction.structured_data?.items?.length || 0;
      showToast(`Extracted ${itemCount} items from PDF`, 'success');

    } catch (err) {
      console.error('Local extraction error:', err);
      showToast(err.message || 'Extraction failed', 'error');
      setCurrentStep(STEPS.DEFINE_REGION);
    } finally {
      setProcessing(false);
      setOcrProgress(0);
      setProcessingStatus('');
    }
  };

  // Method 1: Extract using PDF text layer (best for selectable text)
  const extractWithPdfText = async (filename) => {
    setProcessingStatus('Extracting text from PDF...');
    
    const pdfFile = selectedLocalFile;
    console.log('📄 PDF Text Layer extraction starting...');
    console.log('  - File:', pdfFile?.name);
    console.log('  - Region:', regionCoords);
    
    if (!pdfFile) {
      throw new Error('No PDF file selected');
    }
    
    // Extract text from the selected region
    const textResult = await extractTextFromRegion(pdfFile, 1, regionCoords);
    console.log('  - Text result:', textResult);
    
    if (!textResult.success) {
      throw new Error(textResult.error || 'Failed to extract text from PDF');
    }
    
    if (!textResult.rawText || textResult.rawText.trim() === '') {
      console.warn('⚠️ No text found in PDF text layer. PDF might be scanned/image-based.');
      throw new Error('No text found in selected region. Try OCR method for scanned PDFs.');
    }
    
    setProcessingStatus('Parsing table structure...');
    
    // Use smart parsing to structure the data
    const rawText = textResult.lines.join('\n');
    console.log('  - Raw text extracted:', rawText.substring(0, 500) + '...');
    const parsed = smartParse(rawText, 'PIPING_ISO');
    console.log('  - Parsed result:', parsed);
    
    return {
      id: `local-${Date.now()}`,
      source_filename: filename,
      page_number: 1,
      status: 'completed',
      extracted_data: {
        rawText: rawText,
        confidence: textResult.confidence
      },
      structured_data: {
        success: true,
        items: parsed.items || [],
        headers: parsed.headers || [],
        itemCount: parsed.items?.length || 0,
        parseMethod: 'pdf-text-layer'
      },
      created_at: new Date().toISOString()
    };
  };

  // Method 2: Extract using OCR + smart mapping
  const extractWithOcr = async (filename) => {
    console.log('🔍 OCR extraction starting...');
    console.log('  - Region:', regionCoords);
    
    const canvas = pdfCanvasGetterRef.current?.();
    console.log('  - Canvas:', canvas);
    console.log('  - Canvas size:', canvas?.width, 'x', canvas?.height);
    
    if (!canvas) {
      throw new Error('PDF canvas not ready. Please wait and try again.');
    }
    
    setProcessingStatus('Running OCR...');
    
    // Perform OCR on the selected region
    const ocrResult = await performOcrOnRegion(canvas, regionCoords, {
      onProgress: (progress) => setOcrProgress(progress)
    });
    
    console.log('  - OCR result:', ocrResult);
    console.log('  - OCR text:', ocrResult.text?.substring(0, 500));

    if (!ocrResult.success) {
      throw new Error(ocrResult.error || 'OCR failed');
    }
    
    if (!ocrResult.text || ocrResult.text.trim() === '') {
      throw new Error('OCR returned no text. The region might be empty or the image quality is too low.');
    }
    
    setProcessingStatus('Parsing table structure...');

    // Use smart parsing instead of basic table parser
    const parsed = smartParse(ocrResult.text, 'PIPING_ISO');
    console.log('  - Parsed result:', parsed);

    return {
      id: `local-${Date.now()}`,
      source_filename: filename,
      page_number: 1,
      status: 'completed',
      extracted_data: {
        rawText: ocrResult.text,
        confidence: ocrResult.confidence
      },
      structured_data: {
        success: parsed.success,
        items: parsed.items || [],
        headers: parsed.headers || [],
        itemCount: parsed.items?.length || 0,
        parseMethod: 'ocr-smart-mapping'
      },
      created_at: new Date().toISOString()
    };
  };

  // Method 3: Extract using LLM (GPT-4)
  const extractWithLlm = async (filename) => {
    setProcessingStatus('Extracting text from PDF...');
    
    // First, extract text (prefer PDF text layer, fall back to OCR)
    let rawText = '';
    
    const pdfFile = selectedLocalFile;
    if (pdfFile) {
      const textResult = await extractTextFromRegion(pdfFile, 1, regionCoords);
      if (textResult.success && textResult.rawText.trim()) {
        rawText = textResult.lines.join('\n');
      }
    }
    
    // Fall back to OCR if PDF text extraction didn't work
    if (!rawText.trim()) {
      const canvas = pdfCanvasGetterRef.current?.();
      if (canvas) {
        setProcessingStatus('Running OCR...');
        const ocrResult = await performOcrOnRegion(canvas, regionCoords, {
          onProgress: (progress) => setOcrProgress(progress)
        });
        if (ocrResult.success) {
          rawText = ocrResult.text;
        }
      }
    }
    
    if (!rawText.trim()) {
      throw new Error('Could not extract text from PDF');
    }
    
    setProcessingStatus('Processing with GPT-4...');
    setOcrProgress(50);
    
    // Process with LLM
    const llmResult = await processWithLlm(rawText, {
      context: 'piping isometric BOM',
      expectedColumns: [
        'iso_sheet_number', 'type', 'material', 'schedule',
        'size', 'item_code', 'quantity', 'description'
      ]
    });
    
    setOcrProgress(100);
    
    if (!llmResult.success) {
      throw new Error(llmResult.error || 'LLM processing failed');
    }
    
    return {
      id: `local-${Date.now()}`,
      source_filename: filename,
      page_number: 1,
      status: 'completed',
      extracted_data: {
        rawText: rawText,
        confidence: llmResult.confidence || 90
      },
      structured_data: {
        success: true,
        items: llmResult.items || [],
        headers: llmResult.headers || [],
        itemCount: llmResult.items?.length || 0,
        parseMethod: 'llm-gpt4',
        llmNotes: llmResult.notes
      },
      created_at: new Date().toISOString()
    };
  };

  const loadTemplates = async () => {
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET', 
        `/bom-extraction/templates?jobNumber=${encodeURIComponent(jobNumber || '')}`
      );
      if (response.data.success) {
        setTemplates(response.data.templates || []);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadExtractions = async () => {
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/bom-extraction/extractions?jobNumber=${encodeURIComponent(jobNumber || '')}&limit=50`
      );
      if (response.data.success) {
        setExtractions(response.data.extractions || []);
      }
    } catch (err) {
      console.error('Error loading extractions:', err);
    }
  };

  const handleUploadComplete = useCallback((result) => {
    setBatchId(result.batchId);
    setUploads(result.uploads);
    if (result.uploads.length > 0) {
      setSelectedUpload(result.uploads[0]);
    }
    setCurrentStep(STEPS.DEFINE_REGION);
    showToast(`${result.uploads.length} file(s) uploaded successfully`, 'success');
  }, [showToast]);

  const handleRegionDefined = useCallback((coords) => {
    setRegionCoords(coords);
  }, []);

  const handleSaveTemplate = async (templateName, description) => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/bom-extraction/templates', {
        jobNumber,
        templateName,
        description,
        regionCoords,
        sampleFilename: selectedUpload?.original_filename,
        isDefault: templates.length === 0
      });
      
      if (response.data.success) {
        showToast('Template saved successfully', 'success');
        loadTemplates();
      }
    } catch (err) {
      console.error('Error saving template:', err);
      showToast('Failed to save template', 'error');
    }
  };

  const handleApplyTemplate = (template) => {
    if (template.region_coords) {
      const coords = typeof template.region_coords === 'string' 
        ? JSON.parse(template.region_coords) 
        : template.region_coords;
      setRegionCoords(coords);
      showToast(`Applied template: ${template.template_name}`, 'success');
    }
  };

  const handleStartExtraction = async () => {
    if (!batchId || !regionCoords) {
      showToast('Please upload files and define a region first', 'warning');
      return;
    }

    setProcessing(true);
    setCurrentStep(STEPS.PROCESSING);

    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/bom-extraction/process', {
        batchId,
        regionCoords,
        pageNumbers: null // Process all pages
      });

      if (response.data.success) {
        setProcessingJobId(response.data.jobId);
        showToast('Extraction started', 'success');
        
        // Poll for completion
        pollForCompletion(response.data.jobId);
      }
    } catch (err) {
      console.error('Error starting extraction:', err);
      showToast(err.response?.data?.error || 'Failed to start extraction', 'error');
      setProcessing(false);
      setCurrentStep(STEPS.DEFINE_REGION);
    }
  };

  const pollForCompletion = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await authenticatedApiCall(
          instance, accounts, 'GET',
          `/bom-extraction/results/${jobId}`
        );
        
        if (response.data.extractions && response.data.extractions.length > 0) {
          const allCompleted = response.data.extractions.every(
            e => e.status === 'completed' || e.status === 'failed'
          );
          
          if (allCompleted) {
            clearInterval(pollInterval);
            setProcessing(false);
            setExtractions(response.data.extractions);
            setCurrentStep(STEPS.RESULTS);
            loadExtractions();
            showToast('Extraction completed!', 'success');
          }
        }
      } catch (err) {
        console.error('Error polling for results:', err);
      }
    }, 3000);

    // Timeout after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (processing) {
        setProcessing(false);
        showToast('Extraction timed out. Check results later.', 'warning');
        setCurrentStep(STEPS.RESULTS);
        loadExtractions();
      }
    }, 600000);
  };

  const handleExport = async (extractionIds) => {
    try {
      // Check if these are local extractions (IDs start with "local-")
      const hasLocalIds = extractionIds.some(id => String(id).startsWith('local-'));
      
      if (hasLocalIds) {
        // Export locally - create CSV from state
        const selectedExtractions = extractions.filter(e => extractionIds.includes(e.id));
        
        // Build CSV content
        let csvContent = 'Source File,Part Number,Description,Quantity,Unit,Material,Size,Schedule,Rating\n';
        
        selectedExtractions.forEach(extraction => {
          const items = extraction.structured_data?.items || [];
          const filename = extraction.source_filename || 'Unknown';
          
          items.forEach(item => {
            const row = [
              filename,
              item.part_number || item.column_1 || '',
              item.description || item.column_2 || '',
              item.quantity || item.column_3 || '',
              item.unit || '',
              item.material || '',
              item.size || '',
              item.schedule || '',
              item.rating || ''
            ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
            csvContent += row + '\n';
          });
        });
        
        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bom-extraction-${jobNumber || 'export'}-${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        showToast('Export downloaded (CSV)', 'success');
      } else {
        // Server-side export for database extractions
        const response = await authenticatedApiCall(
          instance, accounts, 'POST',
          '/bom-extraction/export',
          { extractionIds, jobNumber },
          { responseType: 'blob' }
        );

        // Download the file
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bom-extraction-${jobNumber || 'export'}-${Date.now()}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);

        showToast('Export downloaded', 'success');
      }
    } catch (err) {
      console.error('Error exporting:', err);
      showToast('Failed to export', 'error');
    }
  };

  const handleStartOver = () => {
    setCurrentStep(STEPS.UPLOAD);
    setUploads([]);
    setBatchId(null);
    setSelectedUpload(null);
    setRegionCoords(null);
    setProcessingJobId(null);
  };

  // Extraction method selector
  const renderMethodSelector = () => {
    return (
      <div style={styles.methodSelector}>
        <div style={styles.methodHeader}>
          <span style={styles.methodIcon}>⚙️</span>
          <strong>Extraction Method</strong>
        </div>
        <div style={styles.methodOptions}>
          {Object.values(EXTRACTION_METHODS).map(method => (
            <label 
              key={method.id} 
              style={{
                ...styles.methodOption,
                ...(extractionMethod === method.id ? styles.methodOptionSelected : {})
              }}
            >
              <input
                type="radio"
                name="extractionMethod"
                value={method.id}
                checked={extractionMethod === method.id}
                onChange={(e) => setExtractionMethod(e.target.value)}
                style={styles.methodRadio}
              />
              <div style={styles.methodContent}>
                <div style={styles.methodName}>
                  <span>{method.icon}</span>
                  <span>{method.name}</span>
                  {method.recommended && <span style={styles.recommendedBadge}>Recommended</span>}
                  {method.requiresApiKey && !apiKeyValid && (
                    <span style={styles.apiKeyBadge} onClick={(e) => { e.preventDefault(); setShowApiKeyModal(true); }}>
                      🔑 Set API Key
                    </span>
                  )}
                  {method.requiresApiKey && apiKeyValid && (
                    <span style={styles.apiKeyValidBadge}>✓ API Key Set</span>
                  )}
                </div>
                <p style={styles.methodDescription}>{method.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  // API Key Modal
  const renderApiKeyModal = () => {
    if (!showApiKeyModal) return null;
    
    const currentProvider = LLM_PROVIDERS[selectedLlmProvider.toUpperCase()] || LLM_PROVIDERS.GROQ;
    
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modal}>
          <h3 style={styles.modalTitle}>🔑 LLM API Key</h3>
          
          {/* Provider selector */}
          <div style={styles.providerSelector}>
            {Object.values(LLM_PROVIDERS).map(provider => (
              <label 
                key={provider.id}
                style={{
                  ...styles.providerOption,
                  ...(selectedLlmProvider === provider.id ? styles.providerOptionSelected : {})
                }}
              >
                <input
                  type="radio"
                  name="llmProvider"
                  value={provider.id}
                  checked={selectedLlmProvider === provider.id}
                  onChange={(e) => setSelectedLlmProvider(e.target.value)}
                />
                <div>
                  <strong>{provider.name}</strong>
                  {provider.free && <span style={styles.freeBadge}>FREE</span>}
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                    {provider.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
          
          <p style={styles.modalText}>
            Get your API key from: <a href={currentProvider.keyUrl} target="_blank" rel="noopener noreferrer">
              {currentProvider.keyUrl.replace('https://', '')}
            </a>
          </p>
          
          <input
            type="password"
            placeholder={selectedLlmProvider === 'groq' ? 'gsk_...' : 'sk-...'}
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            style={styles.apiKeyInput}
          />
          
          <p style={styles.modalNote}>
            💡 Your API key is stored locally in your browser and never sent to our servers.
            {selectedLlmProvider === 'groq' && ' Groq is completely free to use!'}
          </p>
          
          <div style={styles.modalButtons}>
            <button 
              style={styles.secondaryButton}
              onClick={() => { setShowApiKeyModal(false); setApiKeyInput(''); }}
            >
              Cancel
            </button>
            <button 
              style={styles.primaryButton}
              onClick={handleSaveApiKey}
              disabled={!apiKeyInput.trim()}
            >
              Save API Key
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Step indicator
  const renderStepIndicator = () => (
    <div style={styles.stepIndicator}>
      {Object.values(STEPS).map((step, index) => (
        <React.Fragment key={step}>
          <div style={{
            ...styles.step,
            ...(currentStep === step ? styles.stepActive : {}),
            ...(Object.values(STEPS).indexOf(currentStep) > index ? styles.stepCompleted : {})
          }}>
            <div style={styles.stepNumber}>{index + 1}</div>
            <span style={styles.stepLabel}>
              {step === STEPS.UPLOAD && 'Upload PDFs'}
              {step === STEPS.DEFINE_REGION && 'Define Region'}
              {step === STEPS.PROCESSING && 'Processing'}
              {step === STEPS.RESULTS && 'Results'}
            </span>
          </div>
          {index < Object.values(STEPS).length - 1 && (
            <div style={styles.stepConnector} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={styles.title}>BOM Extraction</h2>
            <p style={styles.subtitle}>
              Extract Bill of Materials data from PDF documents using AI
            </p>
          </div>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        </div>
      </div>

      {renderMethodSelector()}
      {renderStepIndicator()}
      {renderApiKeyModal()}

      <div style={styles.content}>
        {/* Step 1: Upload / Select Files */}
        {currentStep === STEPS.UPLOAD && (
          useLocalMode ? (
            // Local mode: Simple file picker
            <div style={styles.localUploader}>
              <div 
                style={styles.dropZone}
                onClick={() => document.getElementById('local-file-input').click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#007bff'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = '#ddd'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#ddd';
                  handleLocalFilesSelected(e.dataTransfer.files);
                }}
              >
                <input
                  id="local-file-input"
                  type="file"
                  accept="application/pdf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleLocalFilesSelected(e.target.files)}
                />
                <div style={styles.dropIcon}>📄</div>
                <h3 style={styles.dropTitle}>Select PDF files</h3>
                <p style={styles.dropSubtitle}>
                  Click to browse or drag & drop • Processing happens locally in your browser
                </p>
              </div>
            </div>
          ) : (
            // Server mode: Use PdfUploader for server upload
            <PdfUploader
              jobNumber={jobNumber}
              onUploadComplete={handleUploadComplete}
              disabled={!serviceStatus?.services?.documentIntelligence}
            />
          )
        )}

        {/* Step 2: Define Region */}
        {currentStep === STEPS.DEFINE_REGION && (selectedUpload || selectedLocalFile) && (
          <div style={styles.regionStep}>
            <PdfRegionSelector
              uploadId={useLocalMode ? null : selectedUpload?.id}
              pdfFile={useLocalMode ? selectedLocalFile : null}
              filename={useLocalMode ? selectedLocalFile?.name : selectedUpload?.original_filename}
              regionCoords={regionCoords}
              onRegionChange={handleRegionDefined}
              templates={templates}
              onApplyTemplate={handleApplyTemplate}
              onSaveTemplate={handleSaveTemplate}
              onGetPdfCanvas={(getter) => { pdfCanvasGetterRef.current = getter; }}
            />

            <div style={styles.actionBar}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  handleStartOver();
                  setLocalFiles([]);
                  setSelectedLocalFile(null);
                }}
              >
                ← Back to Upload
              </button>

              <div style={styles.actionRight}>
                {/* File selector for multiple files */}
                {(useLocalMode ? localFiles.length > 1 : uploads.length > 1) && (
                  <select
                    style={styles.select}
                    value={useLocalMode ? localFiles.indexOf(selectedLocalFile) : selectedUpload?.id}
                    onChange={(e) => {
                      if (useLocalMode) {
                        setSelectedLocalFile(localFiles[parseInt(e.target.value)]);
                      } else {
                        const upload = uploads.find(u => u.id === e.target.value);
                        setSelectedUpload(upload);
                      }
                    }}
                  >
                    {useLocalMode 
                      ? localFiles.map((f, idx) => (
                          <option key={idx} value={idx}>{f.name}</option>
                        ))
                      : uploads.map(u => (
                          <option key={u.id} value={u.id}>{u.original_filename}</option>
                        ))
                    }
                  </select>
                )}

                <button
                  style={{
                    ...styles.primaryButton,
                    ...(regionCoords ? {} : styles.buttonDisabled)
                  }}
                  onClick={handleLocalExtraction}
                  disabled={!regionCoords}
                >
                  {EXTRACTION_METHODS[Object.keys(EXTRACTION_METHODS).find(
                    k => EXTRACTION_METHODS[k].id === extractionMethod
                  )]?.icon || '📄'} Extract BOM →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {currentStep === STEPS.PROCESSING && (
          <div style={styles.processingStep}>
            <div style={styles.spinner} />
            <h3>
              {extractionMethod === EXTRACTION_METHODS.PDF_TEXT.id && '📄 Extracting Text...'}
              {extractionMethod === EXTRACTION_METHODS.OCR_SMART.id && '🔍 Running OCR...'}
              {extractionMethod === EXTRACTION_METHODS.LLM_GPT4.id && '🤖 Processing with GPT-4...'}
            </h3>
            <p>{processingStatus || 'Processing...'}</p>
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: `${ocrProgress}%`}} />
              </div>
              <span style={styles.progressText}>{ocrProgress}%</span>
            </div>
            <p style={styles.processingNote}>
              {extractionMethod === EXTRACTION_METHODS.PDF_TEXT.id && 'Extracting text directly from PDF (fastest & most accurate)'}
              {extractionMethod === EXTRACTION_METHODS.OCR_SMART.id && 'Processing happens in your browser using Tesseract.js'}
              {extractionMethod === EXTRACTION_METHODS.LLM_GPT4.id && 'Using GPT-4 to intelligently structure the data'}
            </p>
          </div>
        )}

        {/* Step 4: Results */}
        {currentStep === STEPS.RESULTS && (
          <div style={styles.resultsStep}>
            <div style={styles.resultsHeader}>
              <button 
                style={styles.secondaryButton}
                onClick={handleStartOver}
              >
                + New Extraction
              </button>
              
              <button
                style={styles.primaryButton}
                onClick={() => handleExport(extractions.map(e => e.id))}
                disabled={extractions.length === 0}
              >
                Export to Excel
              </button>
            </div>
            
            <ExtractionResultsTable
              extractions={extractions}
              onRefresh={loadExtractions}
              onExport={handleExport}
            />
          </div>
        )}

        {/* Show previous extractions on upload step */}
        {currentStep === STEPS.UPLOAD && extractions.length > 0 && (
          <div style={styles.previousExtractions}>
            <h3 style={styles.sectionTitle}>Previous Extractions</h3>
            <ExtractionResultsTable
              extractions={extractions}
              onRefresh={loadExtractions}
              onExport={handleExport}
              compact
            />
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.type === 'error' ? '#dc3545' : 
                          toast.type === 'warning' ? '#ffc107' : '#28a745'
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    marginBottom: '24px'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  subtitle: {
    margin: 0,
    color: '#666',
    fontSize: '14px'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#666'
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    marginBottom: '24px'
  },
  warningIcon: {
    fontSize: '20px'
  },
  localModeInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#d4edda',
    border: '1px solid #28a745',
    borderRadius: '8px',
    marginBottom: '24px'
  },
  localModeIcon: {
    fontSize: '20px'
  },
  localUploader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  dropZone: {
    border: '2px dashed #ddd',
    borderRadius: '12px',
    padding: '48px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#fafafa'
  },
  dropIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  dropTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#333'
  },
  dropSubtitle: {
    margin: 0,
    color: '#666',
    fontSize: '14px'
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '300px',
    margin: '16px auto'
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007bff',
    transition: 'width 0.3s ease'
  },
  progressText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#007bff',
    minWidth: '40px'
  },
  // Method selector styles
  methodSelector: {
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px'
  },
  methodHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '16px'
  },
  methodIcon: {
    fontSize: '20px'
  },
  methodOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  methodOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  methodOptionSelected: {
    borderColor: '#007bff',
    backgroundColor: '#f0f7ff'
  },
  methodRadio: {
    marginTop: '4px'
  },
  methodContent: {
    flex: 1
  },
  methodName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  methodDescription: {
    margin: 0,
    fontSize: '13px',
    color: '#666'
  },
  recommendedBadge: {
    fontSize: '11px',
    backgroundColor: '#28a745',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '10px'
  },
  apiKeyBadge: {
    fontSize: '11px',
    backgroundColor: '#ffc107',
    color: '#333',
    padding: '2px 8px',
    borderRadius: '10px',
    cursor: 'pointer'
  },
  apiKeyValidBadge: {
    fontSize: '11px',
    backgroundColor: '#28a745',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '10px'
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  modalTitle: {
    margin: '0 0 16px 0',
    fontSize: '20px'
  },
  modalText: {
    margin: '0 0 16px 0',
    color: '#666'
  },
  modalList: {
    margin: '0 0 16px 0',
    paddingLeft: '20px',
    color: '#666'
  },
  apiKeyInput: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '12px',
    boxSizing: 'border-box'
  },
  modalNote: {
    margin: '0 0 16px 0',
    fontSize: '13px',
    color: '#666',
    backgroundColor: '#f0f7ff',
    padding: '12px',
    borderRadius: '8px'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  },
  providerSelector: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px'
  },
  providerOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  providerOptionSelected: {
    borderColor: '#007bff',
    backgroundColor: '#f0f7ff'
  },
  freeBadge: {
    marginLeft: '8px',
    fontSize: '11px',
    backgroundColor: '#28a745',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '10px'
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '32px',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: 'transparent',
    transition: 'all 0.2s'
  },
  stepActive: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  stepCompleted: {
    backgroundColor: '#28a745',
    color: 'white'
  },
  stepNumber: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: '500'
  },
  stepConnector: {
    width: '40px',
    height: '2px',
    backgroundColor: '#ddd',
    margin: '0 8px'
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '24px'
  },
  regionStep: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '20px',
    borderTop: '1px solid #eee'
  },
  actionRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  primaryButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  secondaryButton: {
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px'
  },
  processingStep: {
    textAlign: 'center',
    padding: '60px 20px'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite'
  },
  processingNote: {
    color: '#666',
    fontSize: '13px',
    marginTop: '8px'
  },
  resultsStep: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  previousExtractions: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #eee'
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  toast: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    padding: '12px 24px',
    color: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1000,
    animation: 'fadeIn 0.3s'
  }
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
if (!document.head.querySelector('[data-bom-styles]')) {
  styleSheet.setAttribute('data-bom-styles', 'true');
  document.head.appendChild(styleSheet);
}

export default BomExtractionPage;

